/**
 * Anonymous shared view (Feature #4 + CC_10 D-57). Opens a `/s/<slug>#<key>`
 * link: fetches the ciphertext, decrypts it in the browser with the key from the
 * URL fragment, and shows the frozen snapshot read-only. No login, no guard.
 * Uniform across kinds: a board renders as the frozen Excalidraw scene, a list
 * as a read-only table, a note as rendered markdown — the kind is carried in the
 * decrypted payload (`payload.kind`); a payload without a kind is a legacy board.
 */

import { Excalidraw, MainMenu } from "@excalidraw/excalidraw";
import { useEffect, useState } from "react";

import type { ExcalidrawInitialDataState } from "@excalidraw/excalidraw/types";

import { openEncryptedSnapshot, parseShareUrl } from "../share";
import { validateRyfeEmbeddable } from "../embed-allowlist";
import { renderRyfeEmbeddable } from "../embed-render";
import { renderMarkdown } from "../markdown";

import { PublicChrome } from "./PublicChrome";

import "./ryfe.scss";

// Fidelity (17/18/19) — die gemeinsame Fuß-Bildunterschrift der geteilten Ansicht.
const SHARE_FOOTER = "Geteilte Ansicht — anonym und eingefroren.";

// The anonymous share is read-only; make doubly sure no local-file operation
// (open/save/export-scene) is offered here (Block 1.1). Image export stays.
const SHARED_UI_OPTIONS = {
  canvasActions: {
    loadScene: false,
    saveToActiveFile: false,
    export: false as const,
    saveAsImage: true,
    toggleTheme: true,
    clearCanvas: false,
    changeViewBackgroundColor: false,
  },
};

interface ListSnapshot {
  kind: "list";
  name?: string;
  columns?: { id: string; name: string }[];
  rows?: { id: string; seq?: number; data: Record<string, unknown> }[];
}
interface MdSnapshot {
  kind: "md";
  name?: string;
  content?: string;
}

export const RyfeShared = ({ url }: { url?: string }) => {
  const [payload, setPayload] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const target = url ?? window.location.href;
    const parsed = parseShareUrl(target);
    if (!parsed) {
      setError("Invalid share link");
      return;
    }
    openEncryptedSnapshot(parsed.slug, parsed.key)
      .then((p) => setPayload(p))
      .catch(() => setError("This link is invalid or has expired"));
  }, [url]);

  if (error) {
    return <div className="ryfe-muted">{error}</div>;
  }
  if (!payload) {
    return <div className="ryfe-muted">Lädt geteilten Inhalt…</div>;
  }

  // Read-only list snapshot (D-57): frozen columns + rows, no editing.
  if (payload.kind === "list") {
    const snap = payload as unknown as ListSnapshot;
    const columns = snap.columns ?? [];
    const rows = snap.rows ?? [];
    return (
      <PublicChrome
        title={snap.name || "Liste"}
        badge="Leser"
        footer={SHARE_FOOTER}
      >
        <div className="ryfe-shared-doc">
          <div className="ryfe-table-wrap">
            <table className="ryfe-table ryfe-table--list">
              <thead>
                <tr>
                  <th>#</th>
                  {columns.map((c) => (
                    <th key={c.id}>{c.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.seq ?? ""}</td>
                    {columns.map((c) => (
                      <td key={c.id}>
                        {r.data[c.id] === undefined || r.data[c.id] === null
                          ? ""
                          : String(r.data[c.id])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </PublicChrome>
    );
  }

  // Read-only markdown snapshot (D-57): rendered content (escape-first renderer).
  if (payload.kind === "md") {
    const snap = payload as unknown as MdSnapshot;
    return (
      <PublicChrome
        title={snap.name || "Notiz"}
        badge="Leser"
        footer={SHARE_FOOTER}
      >
        <div className="ryfe-shared-doc">
          <div
            className="ryfe-md__preview"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: renderMarkdown(snap.content ?? ""),
            }}
          />
        </div>
      </PublicChrome>
    );
  }

  // Default: a board snapshot (payload without a kind is a legacy board scene).
  const data: ExcalidrawInitialDataState = {
    elements:
      (payload.elements as ExcalidrawInitialDataState["elements"]) ?? [],
    appState:
      (payload.appState as ExcalidrawInitialDataState["appState"]) ?? {},
    files: (payload.files as ExcalidrawInitialDataState["files"]) ?? {},
  };
  return (
    <PublicChrome
      variant="board"
      title={(payload.name as string) || "Board"}
      badge="Leser"
    >
      {/* Fidelity (17) — E2E-Hinweis: der Schlüssel liegt nur im URL-Fragment. */}
      <div className="ryfe-public__banner">
        🔒 Ende-zu-Ende verschlüsselt — der Schlüssel steht im URL-Fragment und
        erreicht den Server nie.
      </div>
      <div className="ryfe-board__editor">
        <Excalidraw
          initialData={data}
          viewModeEnabled
          UIOptions={SHARED_UI_OPTIONS}
          validateEmbeddable={validateRyfeEmbeddable}
          renderEmbeddable={renderRyfeEmbeddable}
        >
          <MainMenu>
            <MainMenu.DefaultItems.SaveAsImage />
            <MainMenu.DefaultItems.Help />
          </MainMenu>
        </Excalidraw>
      </div>
    </PublicChrome>
  );
};
